import apiUseCases from "../../api";
import { getAuthor } from "../get-author";
import { getOrganization } from "../get-organization";
import { getMachineStructureNodeset } from "../../dependency-injection-container/get-machine-structure-nodeset";
import { getTileCollection } from "../../dependency-injection-container/get-tile-collection";
import { BlueprintDashboardDataBuilder } from "../../node-manager/blueprint-dashboard-data-builder";
import { createRootNodeForBlueprint } from "../../use-cases/create-root-node-for-blueprint";
import store from "@src/reducers/store";
import { getAlarmProfilesUseCase } from "@src/cloud-connect/alarms/use-cases/get-alarm-profile-use-case";
import { parseAxiosApiError } from "@src/cloud-connect/utils/parse-axios-api-error";

export interface ApiNotifications {
  active: boolean;
  recipients: string[];
}

export interface ApiNode {
  id: string;
  name: string;
  unit: string;
  type: string;
}

export interface ApiConditions {
  user_input: string;
  operator: string;
  case_sensitive: boolean;
}

export interface ApiCriteria {
  conditions: ApiConditions[];
  join_operator: string;
}

export interface ApiAlarm {
  active: boolean;
  criteria: ApiCriteria;
  node: ApiNode;
}

export interface ApiAlarmProfile {
  active: boolean;
  client_id: string;
  name: string;
  description: string;
  notifications: Record<string, ApiNotifications>;
  activeAlarms: Record<string, ApiAlarm>;
  inactiveAlarms: Record<string, ApiAlarm>;
  kpiAlarms?: Record<string, ApiAlarm>;
  namespace: string;
  source_id: string;
  sourceName: string | null;
  sourceType: string | null;
  id: string;
}

const getAlarmProfiles = async (): Promise<ApiAlarmProfile[]> => {
  const data = await getAlarmProfilesUseCase();
  return data.body;
};

export const newSaveBlueprintUseCase = async (
  blueprintName,
  blueprintDescription,
) => {
  try {
    const machineStructureNodeset = getMachineStructureNodeset();

    const objectRootNode = machineStructureNodeset
      .getRootNode()
      .toServerChildrenObjectTree();

    const blueprintRootNode = createRootNodeForBlueprint(objectRootNode);
    const author = getAuthor();
    const organization = getOrganization();

    const dashboard_data = new BlueprintDashboardDataBuilder(
      "",
      getTileCollection().getTiles(),
    ).build();

    const namespace = store.getState().yourSlice.authContext.customerNamespace;

    const alarmProfiles = await getAlarmProfiles();

    const blueprintStructure = await apiUseCases.saveBlueprint({
      name: blueprintName,
      author: author,
      organization: organization,
      description: blueprintDescription,
      root_node: blueprintRootNode,
      dashboard_data: dashboard_data,
      alarm_profiles: alarmProfiles,
      namespace: namespace,
    });

    return blueprintStructure;
  } catch (error) {
    const parsedError = parseAxiosApiError(error, "Failed to save blueprint");
    return {
      error: parsedError.message,
      message: parsedError.message,
      statusCode: parsedError.status,
    };
  }
};
